import React from 'react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import Dropdown from '@SonarCX/components/atoms/Dropdown'
import TaskStatus from '@SonarCX/components/molecules/RenderOptions/TaskStatus'

import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { selectTaskStatus } from '@SonarCX/slices/taskStatus'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import useTaskPermissions from '@SonarCX/hooks/permissions/useTaskPermissions'
import useTaskInputOpen from '@SonarCX/hooks/useTaskInputOpen'
import useStyles from './styles'

const Status = ({ task }) => {
  const dispatch = useAppDispatch()

  const { statuses } = useAppSelector(selectTaskStatus)

  const { updateTask } = useTaskAPI()
  const { disabled } = useTaskPermissions(task)
  const { isOpen, handleClick, handleClose } = useTaskInputOpen(task, 'STATUS')

  const [startingValue, setStartingValue] = React.useState(task.status_id)
  const [value, setValue] = React.useState(startingValue)

  const classes = useStyles({ disabled })

  React.useEffect(() => {
    setStartingValue(task.status_id)
    setValue(task.status_id)
  }, [task.status_id])

  const handleSelect = React.useCallback(
    async value => {
      try {
        handleClose()
        if (startingValue !== value) {
          const newTask = { ...task, status_id: value }
          await updateTask(newTask.id, newTask)
          setValue(value)
          dispatch(AddSuccessAlert('Changes saved'))
        }
      } catch (err) {
        dispatch(AddErrorAlert({ err }))
        setValue(startingValue)
      }
    },
    [dispatch, handleClose, startingValue, task, updateTask]
  )

  const renderChosen = React.useMemo(() => {
    if (!value) {
      return <div>Unassigned</div>
    }

    return <TaskStatus opt={statuses.find(({ id }) => id === value) ?? {}} />
  }, [statuses, value])

  return (
    <div className={classes.root}>
      {isOpen ? (
        <Dropdown
          options={statuses.map(type => type.id)}
          renderOption={id => {
            const opt = statuses.find(s => s.id === id)

            if (opt) {
              return <TaskStatus key={id} opt={opt} />
            }
            return null
          }}
          selected={value}
          onSelect={handleSelect}
          onClose={handleClose}
          hideAdd
          colour
          forceOpen
          disabled={disabled}
        >
          {renderChosen}
        </Dropdown>
      ) : (
        <div onClick={handleClick}>{renderChosen}</div>
      )}
    </div>
  )
}

export default React.memo(Status)
